import React, { useEffect, useState, useMemo } from "react";
import { Calendar, Download, Upload, Snowflake, Sun, Sparkles, Gift, CalendarDays, CalendarRange, FileSpreadsheet, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, API, fmtInt } from "../lib/api";
import { useAuth } from "../lib/auth-context";

const TEMPORADA_ICON = {
  ANUAL:            CalendarRange,
  CURSO:            CalendarDays,
  VERANO:           Sun,
  AGOSTO:           Sun,
  VISPERAS:         Sparkles,
  GRANDES_FESTIVOS: Gift,
  ESPECIAL:         Snowflake,
};
const TEMPORADA_COLOR = {
  ANUAL:            "#14532D",
  CURSO:            "#0EA5E9",
  VERANO:           "#EA580C",
  AGOSTO:           "#F59E0B",
  VISPERAS:         "#7C2D12",
  GRANDES_FESTIVOS: "#991B1B",
  ESPECIAL:         "#475569",
};

export default function Calendarios() {
  const { user } = useAuth();
  const [calendarios, setCalendarios] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [expedInfo, setExpedInfo] = useState(null);
  const [filterLote, setFilterLote] = useState("");
  const [filterLinea, setFilterLinea] = useState("");
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    api.get("/calendarios").then((r) => setCalendarios(r.data));
    api.get("/lineas").then((r) => setLineas(r.data));
    api.get("/expediciones/info").then((r) => setExpedInfo(r.data)).catch(() => setExpedInfo(null));
  }, []);

  const lineasFiltradas = useMemo(
    () => (lineas || []).filter((l) => !filterLote || l.lote === filterLote)
      .sort((a, b) => a.numero_linea.localeCompare(b.numero_linea, "es", { numeric: true })),
    [lineas, filterLote]
  );

  // Lotes disponibles para el usuario: derivados de los datos cargados (ya filtrados por concesiones en backend).
  const lotesDisponibles = useMemo(
    () => Array.from(new Set((lineas || []).map((l) => l.lote).filter(Boolean))).sort(),
    [lineas]
  );

  const lineaActiva = useMemo(() => {
    if (!filterLinea || !calendarios) return null;
    const meta = lineas.find((l) => l.codigo_linea === filterLinea);
    return meta ? { ...meta, calendario: calendarios[filterLinea] } : null;
  }, [filterLinea, calendarios, lineas]);

  // Aggregate global stats
  const agg = useMemo(() => {
    if (!calendarios) return null;
    const codes = lineasFiltradas.map((l) => l.codigo_linea);
    let lab = 0, sab = 0, fes = 0, total = 0;
    codes.forEach((c) => {
      const cal = calendarios[c];
      if (!cal) return;
      lab += cal.exp_laborable_total;
      sab += cal.exp_sabado_total;
      fes += cal.exp_festivo_total;
      total += cal.total_anual;
    });
    return { lab, sab, fes, total };
  }, [calendarios, lineasFiltradas]);

  const chartData = useMemo(() => {
    if (!lineaActiva?.calendario) return [];
    const c = lineaActiva.calendario;
    return [
      { name: "Laborable", "Exp/día": c.exp_laborable_dia, "Exp/año": c.exp_laborable_total, dias: c.dias_laborable },
      { name: "Sábado", "Exp/día": c.exp_sabado_dia, "Exp/año": c.exp_sabado_total, dias: c.dias_sabado },
      { name: "Festivo", "Exp/día": c.exp_festivo_dia, "Exp/año": c.exp_festivo_total, dias: c.dias_festivo },
    ];
  }, [lineaActiva]);

  const importGTFS = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportResult({ state: "uploading" });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/gtfs/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("crtm_auth_token")}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Error");
      setImportResult({ state: "ok", data });
    } catch (e) {
      setImportResult({ state: "error", message: e.message });
    }
  };

  const downloadGTFS = async (lote) => {
    const url = `${API}/gtfs/export${lote ? `?lote=${encodeURIComponent(lote)}` : ""}`;
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("crtm_auth_token")}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = lote ? `gtfs_${lote}.zip` : "gtfs_export.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error("Error descargando GTFS:", e);
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("crtm_auth_token")}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error("Error descargando", filename, e);
    }
  };

  const downloadExcel = (lote) =>
    downloadFile(`${API}/excel/concesion/${encodeURIComponent(lote)}`, `viabilidad_electrica_${lote}.xlsx`);
  const downloadMetodologia = () =>
    downloadFile(`${API}/metodologia/pdf`, "nota_metodologica_VOLT-IA.pdf");

  return (
    <div className="space-y-6" data-testid="calendarios-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Calendarios de servicio</div>
        <h1 className="section-title mt-1">Distribución de expediciones por temporada y tipo de día</h1>
        <p className="section-sub">
          Datos del <strong>Anexo de Expediciones</strong> del anteproyecto cargado. Incluye las temporadas
          (anual, curso escolar, agosto, período estival, vísperas y grandes festivos) y todos los tipos de día (LMXJV, S, DF, F, vísperas, etc.).
        </p>
      </div>

      {/* GTFS panel */}
      <div className="crtm-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="font-heading font-semibold text-[15px] mb-2 flex items-center gap-2">
            <Download size={15} /> Exportar GTFS por lote
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Genera un ZIP GTFS estático con calendario completo del Anexo de Expediciones y horas de salida reales por línea/sentido.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => downloadGTFS(null)}
              className="btn-secondary"
              data-testid="btn-gtfs-export-all"
            >
              <Download size={14} /> Todos mis lotes
            </button>
            {(user?.concesiones?.length ? user.concesiones : lotesDisponibles).map((lote) => (
              <button
                key={lote}
                onClick={() => downloadGTFS(lote)}
                className="btn-primary"
                data-testid={`btn-gtfs-export-${lote}`}
              >
                <Download size={14} /> {lote}
              </button>
            ))}
          </div>
        </div>
        {user?.role === "admin" && (
          <div>
            <h3 className="font-heading font-semibold text-[15px] mb-2 flex items-center gap-2">
              <Upload size={15} /> Importar GTFS
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Carga un ZIP GTFS para validar su estructura. Solo administrador.
            </p>
            <label className="btn-secondary cursor-pointer" data-testid="btn-gtfs-import-label">
              <Upload size={14} /> Seleccionar ZIP
              <input type="file" accept=".zip" className="hidden" onChange={importGTFS} data-testid="gtfs-input" />
            </label>
            {importResult?.state === "ok" && (
              <div className="mt-3 bg-green-50 border border-green-200 text-green-900 rounded p-2 text-xs" data-testid="gtfs-import-ok">
                ZIP válido. {importResult.data.files.length} archivos detectados.
                {Object.entries(importResult.data.counts).slice(0, 4).map(([n, c]) => (
                  <div key={n} className="font-mono">{n}: {c} filas</div>
                ))}
              </div>
            )}
            {importResult?.state === "error" && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-900 rounded p-2 text-xs">{importResult.message}</div>
            )}
          </div>
        )}
      </div>

      {/* Entregables oficiales: Excel por concesión + Nota metodológica PDF */}
      <div className="crtm-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="entregables-card">
        <div>
          <h3 className="font-heading font-semibold text-[15px] mb-2 flex items-center gap-2">
            <FileSpreadsheet size={15} className="text-emerald-700" /> Excel consolidado por concesión
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Entregable contractual: una hoja de Resumen + Líneas con todos los indicadores + Hipótesis técnicas + Matriz de tipologías alternativas + Climatología.
          </p>
          <div className="flex flex-wrap gap-2">
            {(user?.concesiones?.length ? user.concesiones : lotesDisponibles).map((lote) => (
              <button
                key={lote}
                onClick={() => downloadExcel(lote)}
                className="btn-primary"
                data-testid={`btn-excel-export-${lote}`}
              >
                <FileSpreadsheet size={14} /> {lote}.xlsx
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-heading font-semibold text-[15px] mb-2 flex items-center gap-2">
            <FileText size={15} className="text-emerald-700" /> Nota metodológica
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            PDF con criterios de cálculo, hipótesis, fuentes de datos y modelo de viabilidad aplicado.
          </p>
          <button
            onClick={downloadMetodologia}
            className="btn-primary"
            data-testid="btn-metodologia-pdf"
          >
            <FileText size={14} /> Descargar nota metodológica (PDF)
          </button>
        </div>
      </div>

      {/* Temporadas reales del Anexo V */}
      {expedInfo && (
        <div className="crtm-card" data-testid="temporadas-card">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
              <CalendarRange size={16} className="text-emerald-700" />
              Temporadas detectadas en el anteproyecto
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {fmtInt(expedInfo.n_expediciones_total)} expediciones / año en {expedInfo.n_lineas} líneas ·{" "}
              <strong>{fmtInt(expedInfo.veh_km_anual_total)} veh-km / año</strong> ·{" "}
              {fmtInt(expedInfo.veh_h_anual_total)} veh-h / año. Las variantes literales del PDF se agrupan en 7 categorías canónicas
              (cada combinación categoría × tipo de día genera un <code className="bg-gray-100 px-1 rounded">service_id</code> independiente en el export GTFS).
            </p>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(expedInfo.categorias).map(([catName, meta]) => {
              const agg = expedInfo.agregado_por_categoria[catName] || {n_salidas: 0, veh_km: 0};
              const n = agg.n_salidas;
              const km = agg.veh_km;
              if (n === 0) return null;
              const Icon = TEMPORADA_ICON[meta.service_id] || Calendar;
              const color = meta.color || TEMPORADA_COLOR[meta.service_id] || "#6B7280";
              const pct = ((n / expedInfo.n_expediciones_total) * 100).toFixed(1);
              return (
                <div
                  key={catName}
                  data-testid={`temporada-${meta.service_id}`}
                  className="rounded-lg border border-gray-100 p-4 hover:shadow-sm transition-shadow"
                  style={{ borderLeft: `4px solid ${color}` }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ background: `${color}15` }}
                    >
                      <Icon size={20} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-wider font-bold" style={{ color }}>
                        {meta.service_id}
                      </div>
                      <div className="text-sm font-semibold text-gray-900 mt-0.5">{meta.descripcion}</div>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">{fmtInt(n)}</span>
                        <span className="text-xs text-gray-500">exp · {pct}%</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{fmtInt(km)} veh-km</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tipos de día */}
          <div className="px-5 pb-5">
            <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-3 mt-3">
              Tipos de día detectados
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(expedInfo.agregado_por_tipo_dia)
                .sort((a, b) => b[1] - a[1])
                .map(([td, n]) => (
                  <div
                    key={td}
                    className="px-3 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-xs"
                    data-testid={`tipo-dia-${td.replace(/\s+/g, '_')}`}
                  >
                    <span className="font-mono font-bold text-gray-800">{td}</span>
                    <span className="text-gray-500 ml-2">{fmtInt(n)} exp</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Aggregate KPIs */}
      {agg && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="Total anual" value={fmtInt(agg.total)} sub="Expediciones" />
          <KPI label="Laborables" value={fmtInt(agg.lab)} sub={`${((agg.lab / agg.total) * 100).toFixed(0)}% del total`} />
          <KPI label="Sábados" value={fmtInt(agg.sab)} sub={`${((agg.sab / agg.total) * 100).toFixed(0)}% del total`} />
          <KPI label="Festivos / domingos" value={fmtInt(agg.fes)} sub={`${((agg.fes / agg.total) * 100).toFixed(0)}% del total`} />
        </div>
      )}

      {/* Filter */}
      <div className="crtm-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="kpi-label">Lote</label>
            <select value={filterLote} onChange={(e) => { setFilterLote(e.target.value); setFilterLinea(""); }}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white" data-testid="cal-filter-lote">
              <option value="">Todos</option>
              {(user?.concesiones?.length ? user.concesiones : lotesDisponibles).map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="kpi-label">Línea</label>
            <select value={filterLinea} onChange={(e) => setFilterLinea(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white" data-testid="cal-filter-linea">
              <option value="">Selecciona…</option>
              {lineasFiltradas.map((l) => {
                const label = `${l.lote} · ${l.numero_linea} — ${l.denominacion.slice(0, 40)}`;
                return <option key={l.codigo_linea} value={l.codigo_linea}>{label}</option>;
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Detail per linea */}
      {lineaActiva?.calendario && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="crtm-card p-5">
            <h3 className="font-heading font-semibold text-[15px]">
              Línea {lineaActiva.numero_linea} <span className="text-gray-500 font-normal">— {lineaActiva.denominacion}</span>
            </h3>
            <p className="text-xs text-gray-500 mt-1">{lineaActiva.tipo_servicio} · Total anual: {fmtInt(lineaActiva.calendario.total_anual)} expediciones</p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 20 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="Exp/día" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={48} />
                <Bar yAxisId="right" dataKey="Exp/año" fill="#0F1A14" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="crtm-card overflow-hidden">
            <table className="crtm-table">
              <thead>
                <tr>
                  <th>Tipo día</th>
                  <th>Días/año</th>
                  <th>Exp/día</th>
                  <th>Exp/año</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((c) => {
                  const pct = ((c["Exp/año"] / lineaActiva.calendario.total_anual) * 100).toFixed(1);
                  return (
                    <tr key={c.name}>
                      <td className="font-semibold">{c.name}</td>
                      <td className="font-mono">{c.dias}</td>
                      <td className="font-mono">{fmtInt(c["Exp/día"])}</td>
                      <td className="font-mono font-semibold">{fmtInt(c["Exp/año"])}</td>
                      <td className="font-mono text-xs text-gray-600">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-900 text-white font-semibold">
                  <td>TOTAL</td>
                  <td className="font-mono">365</td>
                  <td>—</td>
                  <td className="font-mono">{fmtInt(lineaActiva.calendario.total_anual)}</td>
                  <td className="font-mono">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const KPI = ({ label, value, sub }) => (
  <div className="crtm-card p-5">
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{sub}</div>
  </div>
);
